import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { getDepartmentIncidents, closeOverlay, getRisk } from "../../store/actions/riskActions";
import { getIncidentsCompliance, getIncidentsDepartment, getIncident, closeIncident } from "../../store/actions/incidentActions"
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import IncidentDetailsForm from "../Incidents/IncidentOverlay";

/** Map known API status strings to i18n labels; pass through any other value from the API. */
const getIncidentStatusLabel = (t, status) => {
  if (status === "Open") return t("incidentManagement.filters.open");
  if (status === "In Progress") return t("incidentManagement.filters.inProgress");
  if (status === "Closed") return t("incidentManagement.filters.closed");
  if (status == null || status === "" || status === "Unknown")
    return t("incidentManagement.unknownStatus");
  return String(status);
};

function IncidentGrid({ data, onOpen, getBarColor }) {
  const { t } = useTranslation();

  if (!data || data?.length === 0) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-2xl py-10 flex flex-col items-center justify-center text-center shadow-sm">
        <p className="text-sm font-medium text-gray-700">{t("incidentManagement.grid.noResults")}</p>
        <p className="text-xs text-gray-500 mt-1">
          {t("incidentManagement.grid.noResultsHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {data.map((incident) => {
          const id = incident.incident_id;
          const title = incident.title ?? t("incidentManagement.grid.untitled");
          const owner = incident.risk_owner_name || t("incidentManagement.notAvailable");
          const status = incident.status || "Unknown";

          const createdAt = incident.report_date
            ? new Date(incident.report_date).toLocaleDateString()
            : t("incidentManagement.notAvailable");

          const closedAt =
            incident.status === "Closed" && incident.closed_at
              ? new Date(incident.closed_at).toLocaleDateString()
              : "-";

          const efficiency = incident.AverageEfficiencyLevel || 0;
          const effLabel =
            incident.AverageEfficiencyLevel !== null &&
              incident.AverageEfficiencyLevel !== undefined
              ? `${parseFloat(incident.AverageEfficiencyLevel).toFixed(0)}%`
              : t("incidentManagement.noControl");

          // status chip styles
          let statusClasses =
            "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ";
          if (status === "Open") {
            statusClasses += "bg-red-50 text-red-700 border-red-100";
          } else if (status === "Closed") {
            statusClasses += "bg-green-50 text-green-700 border-green-100";
          } else {
            statusClasses += "bg-yellow-50 text-yellow-700 border-yellow-100";
          }

          return (
            <div
              key={id}
              className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 flex flex-col justify-between hover:border-gray-300 hover:shadow-md transition cursor-pointer"
              onClick={() => onOpen(incident)}
            >
              {/* Header: ID + title + status */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-gray-500 mb-0.5">
                    {t("incidentManagement.grid.incidentNumber", { id })}
                  </p>
               <p className="text-base leading-snug font-semibold text-gray-900 truncate">
                    {title}
                  </p>
                </div>
                <span className={statusClasses}>{getIncidentStatusLabel(t, status)}</span>
              </div>

              {/* Meta section */}
              <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-400">{t("incidentManagement.columns.riskOwner")}</span>
                  <span className="font-medium truncate">{owner}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-400">{t("incidentManagement.grid.labelCreated")}</span>
                  <span className="font-medium truncate">{createdAt}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-400">{t("incidentManagement.grid.labelClosed")}</span>
                  <span className="font-medium truncate">{closedAt}</span>
                </div>
              </div>

              {/* Control efficiency bar (same as table but card style) */}
              <div className="mt-auto border-t border-gray-100 pt-2">
                <p className="text-[11px] text-gray-500 mb-1">
                  {t("incidentManagement.columns.controlEfficiency")}
                </p>
                <div className="relative w-full h-5 bg-[#E5E7EB] rounded-full flex items-center">
                  <div
                    className="absolute h-full rounded-full"
                    style={{
                      width: `${efficiency || 0}%`,
                      backgroundColor: getBarColor(efficiency),
                    }}
                  />
                  <span className="relative text-xs text-white ml-2">
                    {effLabel}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


const IncidentList = ({
  activeDepartmentId,
  activeDepartment,
  getIncidentFN,
  getDepartmentIncidents,
  department_incidents,
  getIncidentsFN,
  getIncidentsComplianceFN,
  closeOverlayFN,
  getRiskFN,
  closeIncidentFN,
  isCompliance
}) => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [IncidentList, setIncidentList] = useState([]);

  useEffect(() => {
    if (activeDepartmentId && isCompliance !== true) {
      //getDepartmentIncidents(activeDepartmentId);
      getIncidentsFN(activeDepartmentId)
    } else if (activeDepartmentId && isCompliance === true) {
      getIncidentsComplianceFN(activeDepartmentId)
    }
  }, [activeDepartmentId, getDepartmentIncidents]);


  useEffect(() => {
    if (department_incidents) {
      setIncidentList(department_incidents)
    }
  }, [department_incidents]);

  const openPopup = (incident) => {

    setSelectedIncident(incident);
    getIncidentFN(incident.incident_id)
    setIsPopupOpen(true);
  };

  const closePopup = async () => {
    ////console.log("Closed")
    setIsPopupOpen(false);
    closeOverlayFN();
    closeIncidentFN();
    if (isCompliance !== true) {
      await getIncidentsFN(activeDepartmentId);
    } else if (isCompliance === true) {
      await getIncidentsComplianceFN(activeDepartmentId)
    }
  };

  const getBarColor = (value) => {
    if (value >= 75) return '#00AFB9'; // Green for high values
    if (value >= 50) return '#FFD166'; // Yellow for medium values
    if (value > 0) return '#EF476F'; // Red for low values
    return '#f07167'; // Default background color for 0
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");      // "" = All
  const [priorityFilter, setPriorityFilter] = useState("");  // "" = All
  const [viewMode, setViewMode] = useState("table");         // "table" | "grid"

  // safely handle empty list
  const incidents = IncidentList || [];

  // simple client-side search + filters
  const filteredIncidents = incidents.filter((incident) => {
    const q = searchQuery.toLowerCase().trim();

    const matchesSearch =
      !q ||
      String(incident.incident_id || "")
        .toLowerCase()
        .includes(q) ||
      String(incident.title || "")
        .toLowerCase()
        .includes(q) ||
      String(incident.risk_owner_name || "")
        .toLowerCase()
        .includes(q);

    const matchesStatus =
      !statusFilter || statusFilter === "All" || incident.status === statusFilter;

    const matchesPriority =
      !priorityFilter ||
      priorityFilter === "All" ||
      incident.priority === priorityFilter; // assumes priority in data

    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <div className="w-full bg-[#F8F8F8] min-h-[100dvh]">
      <div className="px-6 md:px-10 xl:px-16 2xl:px-24 pt-6 pb-10">

        {/* Page header */}
        <div className="mb-4">
          <h1 className="text-xl md:text-2xl font-medium tracking-wide text-[#0E0E0E]">
            {t("incidentManagement.title")}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            {t("incidentManagement.subtitle")}
          </p>
        </div>

        {/* Toolbar: search + filters + view toggle */}
        <div className="w-full mb-4">
          <div className="w-full bg-white border border-gray-200 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-sm">
            {/* Search */}
            <div className="relative flex-1 min-w-[220px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("incidentManagement.searchPlaceholder")}
                className="
                w-full h-9 pl-9 pr-3 rounded-md bg-[#F5F5F7] text-sm text-gray-800
                outline-none border-none
                focus:outline-none focus:ring-0 focus:border-none
                active:outline-none active:ring-0 active:border-none
              "
              />
            </div>

            {/* Status filter */}
            <div className="relative w-full sm:w-40">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 12h14"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <select
                value={statusFilter || "All"}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="
                w-full h-9 bg-[#F5F5F7] rounded-md pl-8 pr-7 text-xs md:text-sm
                appearance-none
                outline-none border-none
                focus:outline-none focus:ring-0 focus:border-none
                active:outline-none active:ring-0 active:border-none
              "
              >
                <option value="All">{t("incidentManagement.filters.allStatus")}</option>
                <option value="Open">{t("incidentManagement.filters.open")}</option>
                <option value="In Progress">{t("incidentManagement.filters.inProgress")}</option>
                <option value="Closed">{t("incidentManagement.filters.closed")}</option>
              </select>
              <svg
                className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
              </svg>
            </div>

            {/* Priority filter */}
            <div className="relative w-full sm:w-40">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M7 7h10M7 12h6M7 17h4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <select
                value={priorityFilter || "All"}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="
                w-full h-9 bg-[#F5F5F7] rounded-md pl-8 pr-7 text-xs md:text-sm
                appearance-none
                outline-none border-none
                focus:outline-none focus:ring-0 focus:border-none
                active:outline-none active:ring-0 active:border-none
              "
              >
                <option value="All">{t("incidentManagement.filters.allPriority")}</option>
                <option value="Critical">{t("incidentManagement.filters.critical")}</option>
                <option value="High">{t("incidentManagement.filters.high")}</option>
                <option value="Medium">{t("incidentManagement.filters.medium")}</option>
                <option value="Low">{t("incidentManagement.filters.low")}</option>
              </select>
              <svg
                className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
              </svg>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-0 ml-auto border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`flex items-center justify-center w-9 h-9 text-sm transition
                ${viewMode === "table"
                    ? "bg-black text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 6h18M3 12h18M3 18h18"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`flex items-center justify-center w-9 h-9 text-sm border-l border-gray-300 transition
                ${viewMode === "grid"
                    ? "bg-black text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* CONTENT: table for now (we'll add grid later) */}
        {filteredIncidents?.length ? (
          viewMode === "table" ? (
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full table-fixed text-sm text-left text-gray-700 border-collapse">
                <colgroup>
                  <col style={{ width: "6rem" }} />
                  <col style={{ width: "10rem" }} />
                  <col style={{ width: "18rem" }} />
                  <col style={{ width: "8rem" }} />
                  <col style={{ width: "10rem" }} />
                  <col style={{ width: "8rem" }} />
                  <col style={{ width: "10rem" }} />
                </colgroup>

                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 border-b text-sm  text-gray-700">
                      {t("incidentManagement.columns.incidentId")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.riskOwner")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.risk")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.controlEfficiency")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.createdAt")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.status")}
                    </th>
                    <th className="px-3 py-2 border-b text-sm text-gray-700">
                      {t("incidentManagement.columns.closedAt")}
                    </th>
                  </tr>
                </thead>



                <tbody>
                  {filteredIncidents.map((incident) => (
                    <tr
                      key={incident.incident_id}
                      className="hover:bg-[#F8F8F8] transition-colors duration-150 cursor-pointer"
                      onClick={() => openPopup(incident)}
                    >
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        {incident.incident_id}
                      </td>
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        {incident.risk_owner_name || t("incidentManagement.notAvailable")}
                      </td>
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        {incident.title}
                      </td>
                      <td className="px-3 py-2 border-b">
                        <div className="relative w-[100px] h-5 bg-[#E5E7EB] rounded-full flex items-center">
                          <div
                            className="absolute h-full rounded-full"
                            style={{
                              width: `${incident.AverageEfficiencyLevel || 0}%`,
                              backgroundColor: getBarColor(
                                incident.AverageEfficiencyLevel
                              ),
                            }}
                          />
                          <span className="relative text-white ml-2 text-xs">
                            {incident.AverageEfficiencyLevel !== null &&
                              incident.AverageEfficiencyLevel !== undefined
                              ? `${parseFloat(
                                incident.AverageEfficiencyLevel
                              ).toFixed(0)}%`
                              : t("incidentManagement.noControl")}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        {incident.report_date
                          ? new Date(incident.report_date).toLocaleDateString()
                          : t("incidentManagement.notAvailable")}
                      </td>
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${incident.status === "Open"
                            ? "bg-red-100 text-red-800"
                            : incident.status === "Closed"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                            }`}
                        >
                          {getIncidentStatusLabel(t, incident.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2 border-b whitespace-nowrap">
                        {incident.status === "Closed" && incident.closed_at
                          ? new Date(incident.closed_at).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <IncidentGrid
              data={filteredIncidents}
              onOpen={openPopup}
              getBarColor={getBarColor}
            />
          )
        ) : (
          <p className="text-gray-500 mt-4">{t("incidentManagement.emptyState")}</p>
        )}
        {/* Popup stays as-is */}
        <Popup
          open={isPopupOpen}
          onClose={closePopup}
          contentStyle={{
            overflow: "auto",
            maxHeight: "80vh",
            width: "810px",
            marginTop: "10vh",
          }}
        >
          <IncidentDetailsForm
            closeIncident={closePopup}
            currentIncident={selectedIncident}
            not_editable={true}
          />
        </Popup>
      </div>
    </div>
  );


};

const mapStateToProps = (state) => {
  ////console.log("Incidnet List state", state)
  return {
    department_incidents: state.incident.incidents
  }
};

const mapDispatchToProps = (dispatch) => ({
  getDepartmentIncidents: (department_id) => dispatch(getDepartmentIncidents(department_id)),
  getIncidentsFN: (department_id) => dispatch(getIncidentsDepartment(department_id)),
  getIncidentsComplianceFN: (compliance_id) => dispatch(getIncidentsCompliance(compliance_id)),
  getIncidentFN: (incident_id) => dispatch(getIncident(incident_id)),
  closeOverlayFN: () => dispatch(closeOverlay()),
  closeIncidentFN: () => dispatch(closeIncident()),
  getRiskFN: (risk_id) => dispatch(getRisk(risk_id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IncidentList);